import React from 'react';

import { useTranslationContextualAction } from '@lumapps/auto-translation/hooks/useTranslationContextualAction';
import { contentAnalytics } from '@lumapps/content-analytics/routes';
import { doesCustomizationExist, PLACEMENT, Targets } from '@lumapps/customizations/api';
import { DistributeResourceDialog } from '@lumapps/distribute/components/DistributeResourceDialog';
import { useDistributeDialog } from '@lumapps/distribute/hooks/useDistributeDialog';
import { DISTRIBUTE } from '@lumapps/distribute/keys';
import type { DistributeResourceType } from '@lumapps/distribute/types';
import { ContextualActions } from '@lumapps/lumx-front-office/components/ContextualActions';
import { mdiChartLine, mdiMessageAlertOutline, mdiPencil, mdiSend } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { ReportTunnel } from '@lumapps/reports/components/ReportTunnel';
import { useReportTunnel } from '@lumapps/reports/hooks/useReportTunnel';
import { REPORT_ALREADY_REPORTED, REPORTS } from '@lumapps/reports/keys';
import { useResponsive } from '@lumapps/responsive';
import { useQueryParams } from '@lumapps/router';
import { getRouteAsLinkProps } from '@lumapps/router/utils';
import { GLOBAL } from '@lumapps/translations';
import { SAVED_ITEMS_RESOURCE_TYPES } from '@lumapps/user-saved-items/constants';
import { useContextualSave } from '@lumapps/user-saved-items/hooks/useContextualSave';

import { ANCHOR_FROM_FO } from '../../constants';
import { shouldUserBeRedirectedToWriterEditor } from '../../ducks/selectors';
import { useContent } from '../../hooks/useContent';
import { contentEdit } from '../../routes';

export interface ContentContextualActionsProps {
    /** Indicates if the component should be displayed in the current content */
    isEnabled: boolean;
    /** Current content id */
    contentId: string;
    /** Indicates if the user can access analytics and if the content is live */
    canAccessContentAnalytics: boolean;
    /** Indicates if the user can access reports and if the content is live */
    canAccessReports: boolean;
    /** Current content type id */
    contentTypeId?: string;
    /** Is the user in design mode */
    isInDesignerMode?: boolean;
    /** Whether the content is a home page */
    isHomePage?: boolean;
    /** callback to be executed when the translation button is triggered on legacy page
     * @see /apps/legacy/client/front-office/modules/content/common/views/content-front.html
     */
    onTranslation?: (isTranslationActive: boolean) => void;
}

/**
 * This component provide a contextual menu in the product which regroups a list of options related to a content.
 */
export const ContentContextualActions: React.FC<ContentContextualActionsProps> = ({
    isEnabled,
    canAccessContentAnalytics,
    canAccessReports,
    contentId,
    contentTypeId,
    isInDesignerMode,
    isHomePage,
    onTranslation,
}) => {
    const { canCurrentUserEditContent, currentContentId, currentContentInstanceSlug } = useContent();
    const { isMobile } = useResponsive();
    const isEditActionVisible = !isMobile && canCurrentUserEditContent && contentId;
    const isAnalyticsActionVisible = isEditActionVisible && !isMobile && canAccessContentAnalytics && contentTypeId;
    const distributeRef = React.useRef(null);
    const translationContextualAction = useTranslationContextualAction(undefined, onTranslation);

    // Reports
    const reportTunnel = useReportTunnel({
        resourceId: contentId,
        resourceType: 'content',
    });
    const isReportsActionVisible = reportTunnel.isEnabled && !isHomePage && canAccessReports && contentTypeId;

    const { distribute } = useQueryParams();
    const distributeDialogProps = React.useMemo(
        () => ({
            resource: {
                type: 'content' as DistributeResourceType,
                id: currentContentId || '',
            },
        }),
        [currentContentId],
    );
    const {
        formDialogProps: distributeResourceProps,
        isDistributeResourceEnabled,
        openFormDialog: openDistributeDialog,
        isMounted: isDistributeDialogMounted,
    } = useDistributeDialog(distributeDialogProps);
    const isDistributeActionVisible = !isMobile && Boolean(isDistributeResourceEnabled);

    React.useEffect(() => {
        if (distribute === 'true' && isDistributeActionVisible && !isInDesignerMode) {
            openDistributeDialog();
        }
    }, [distribute, isDistributeActionVisible, isInDesignerMode, openDistributeDialog]);

    const isContextualActionsCustomized =
        doesCustomizationExist({ target: Targets.CONTEXTUAL_ACTIONS, placement: PLACEMENT.ABOVE }) ||
        doesCustomizationExist({ target: Targets.CONTEXTUAL_ACTIONS, placement: PLACEMENT.UNDER });

    const { hasUserSavedItemsFeature, action: saveAction } = useContextualSave({
        resourceId: contentId,
        resourceType: SAVED_ITEMS_RESOURCE_TYPES.content,
    });

    const shouldOpenWriterEditor = useSelector(shouldUserBeRedirectedToWriterEditor(contentTypeId));

    const doesNotHaveAnyPermission =
        !isEditActionVisible &&
        !isAnalyticsActionVisible &&
        !translationContextualAction?.translationAction &&
        !isReportsActionVisible &&
        !hasUserSavedItemsFeature &&
        !isDistributeActionVisible;

    // If the user does not have access to any of the options, don't show the Contextual Action component
    if (!isEnabled || isInDesignerMode || (!isContextualActionsCustomized && doesNotHaveAnyPermission)) {
        return null;
    }

    return (
        <>
            <ContextualActions
                actions={[
                    saveAction,
                    translationContextualAction?.translationAction && {
                        ...translationContextualAction.translationAction,
                        dataAttributes: {
                            element: 'content-translate',
                            action: 'translate',
                        },
                    },
                    isReportsActionVisible && {
                        dataAttributes: {
                            element: 'content-report',
                            action: contentId,
                        },
                        isDisabled: reportTunnel.isAlreadyReported,
                        iconSvgPath: mdiMessageAlertOutline,
                        labelKey: REPORTS.REPORT,
                        buttonRef: reportTunnel.reportButtonRef,
                        action: reportTunnel.open,
                        tooltipLabelKey: reportTunnel.isAlreadyReported ? REPORT_ALREADY_REPORTED.content : undefined,
                    },
                    isEditActionVisible && {
                        dataAttributes: {
                            element: 'content-edit',
                            action: contentId,
                        },
                        iconSvgPath: mdiPencil,
                        labelKey: GLOBAL.EDIT,
                        action: getRouteAsLinkProps({
                            ...contentEdit(contentId, {
                                anchor: ANCHOR_FROM_FO,
                                shouldOpenWriterEditor,
                            }),
                            instanceSlug: currentContentInstanceSlug,
                        }),
                    },
                    isAnalyticsActionVisible && {
                        dataAttributes: {
                            element: 'content-analytics',
                            action: contentId,
                        },
                        iconSvgPath: mdiChartLine,
                        labelKey: GLOBAL.ANALYTICS,
                        action: getRouteAsLinkProps({
                            ...contentAnalytics(contentTypeId as string, contentId),
                            instanceSlug: currentContentInstanceSlug,
                        }),
                    },
                    isDistributeActionVisible && {
                        dataAttributes: {
                            element: 'content-distribute',
                            action: contentId,
                        },
                        iconSvgPath: mdiSend,
                        labelKey: DISTRIBUTE.DISTRIBUTE,
                        action: openDistributeDialog,
                        buttonRef: distributeRef,
                    },
                ]}
            />
            <ReportTunnel {...reportTunnel.props} />
            {isDistributeDialogMounted && (
                <DistributeResourceDialog parentElement={distributeRef} {...distributeResourceProps} />
            )}
        </>
    );
};
ContentContextualActions.displayName = 'ContentContextualActions';
